import { useState } from "react";
import { Download, FileText, FileType, Presentation, Volume2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API } from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ExportMenu({ title, content, includePptx = true }) {
  const [busy, setBusy] = useState(null); // "pdf" | "docx" | "pptx" | "tts"
  const [audioUrl, setAudioUrl] = useState(null);

  const exportFile = async (kind) => {
    setBusy(kind);
    try {
      // httpOnly auth cookie is sent automatically via credentials:"include".
      const res = await fetch(`${API}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, content, kind }),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9_-]+/gi, "_").slice(0, 80) || "teebee"}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(null);
    }
  };

  const speak = async () => {
    setBusy("tts");
    try {
      const text = stripMarkdown(content).slice(0, 4000);
      const { data } = await api.post("/voice/tts", { text, voice: "nova" });
      const url = `data:${data.mime_type};base64,${data.audio_base64}`;
      setAudioUrl(url);
      // auto-play
      setTimeout(() => {
        const el = document.getElementById("teebee-tts-audio");
        if (el) el.play().catch(() => {});
      }, 50);
    } catch (e) {
      toast.error("Audio generation failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="export-menu">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} data-testid="export-trigger">
            {busy && busy !== "tts" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Download as</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => exportFile("pdf")} data-testid="export-pdf">
            <FileText className="h-4 w-4 mr-2" /> PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportFile("docx")} data-testid="export-docx">
            <FileType className="h-4 w-4 mr-2" /> DOCX (Word)
          </DropdownMenuItem>
          {includePptx && (
            <DropdownMenuItem onClick={() => exportFile("pptx")} data-testid="export-pptx">
              <Presentation className="h-4 w-4 mr-2" /> PPTX (PowerPoint)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        size="sm"
        variant="outline"
        onClick={speak}
        disabled={!!busy}
        className="rounded-full"
        data-testid="tts-button"
      >
        {busy === "tts" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Volume2 className="h-3.5 w-3.5 mr-1.5" />}
        Listen
      </Button>

      {audioUrl && (
        <audio id="teebee-tts-audio" controls src={audioUrl} className="hidden sm:inline-block max-w-[260px]" />
      )}
    </div>
  );
}

function stripMarkdown(s) {
  if (!s) return "";
  return s
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[*_`~>#-]+/g, " ")
    .replace(/\[(.+?)\]\((.+?)\)/g, "$1")
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
